import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import { normalizeLanguage, useI18n } from '../i18n';
import './GlobalSynapticNodes.css';

const INACTIVITY_LIMIT = 15000; // 15 seconds
const COOLDOWN_LIMIT = 4 * 60 * 1000; // 4 minutes

export function getLocalSynapticFallback(lang, username) {
  const normalized = normalizeLanguage(lang);
  const fallbackName = normalized === 'en' ? 'traveler' : 'viajero';
  const name = username || fallbackName;
  if (normalized === 'en') {
    return {
      question: `Are you still there, ${name}?`,
      responses: ['Yes', 'Always']
    };
  }
  return {
    question: `\u00bfSigues ah\u00ed, ${name}?`,
    responses: ['S\u00ed', 'Siempre']
  };
}

class NodeParticle {
  constructor(x, y) {
    this.x = x;
    this.y = y;
    this.vx = (Math.random() - 0.5) * 10;
    this.vy = (Math.random() - 0.5) * 10;
    this.alpha = 1;
    this.size = Math.random() * 3 + 2;
  }

  update() {
    this.x += this.vx;
    this.y += this.vy;
    this.alpha -= 0.02;
  }

  draw(ctx, color) {
    ctx.save();
    ctx.globalAlpha = this.alpha;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

export default function GlobalSynapticNodes() {
  const { user } = useAuth();
  const { language } = useI18n();
  const location = useLocation();
  const [showNode, setShowNode] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [nodePos, setNodePos] = useState({ x: 0, y: 0 });
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [responses, setResponses] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [modalOffset, setModalOffset] = useState({ x: 0, y: 0 });

  const lastActivityRef = useRef(Date.now());
  const lastShownTimestampRef = useRef(0);
  const canvasRef = useRef(null);
  const expandedRef = useRef(null);
  const particlesRef = useRef([]);
  const animationRef = useRef(null);

  const isEligiblePage = location.pathname === '/profile' || location.pathname === '/acerca';

  const resetInactivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (showNode) {
      setShowNode(false);
      setIsExpanded(false);
    }
  }, [showNode]);

  useEffect(() => {
    if (!isEligiblePage) {
      setShowNode(false);
      return;
    }

    const handleScroll = () => resetInactivity();

    window.addEventListener('scroll', handleScroll);

    const interval = setInterval(async () => {
      const now = Date.now();
      const isCoolingDown = now - lastShownTimestampRef.current < COOLDOWN_LIMIT;

      if (
        now - lastActivityRef.current > INACTIVITY_LIMIT &&
        !showNode &&
        isEligiblePage &&
        !isCoolingDown
      ) {
        // Spawn node
        const x = Math.random() * 80 + 10; // 10% to 90%
        const y = Math.random() * 80 + 10;
        setNodePos({ x, y });

        setShowNode(true);
        setIsFetching(true);
        lastShownTimestampRef.current = now;

        try {
          const res = await api.get('/tareas/synaptic-node', {
            params: {
              path: location.pathname,
              traits: user?.user_traits?.join(',') || '',
              lang: normalizeLanguage(language)
            }
          });
          const fallback = getLocalSynapticFallback(language, user?.username);
          const responseOptions = Array.isArray(res.data.responses) && res.data.responses.length > 0
            ? res.data.responses
            : fallback.responses;
          setCurrentQuestion(res.data.question || fallback.question);
          setResponses(responseOptions);
        } catch (err) {
          console.error("Error fetching synaptic node data:", err);
          const fallback = getLocalSynapticFallback(language, user?.username);
          setCurrentQuestion(fallback.question);
          setResponses(fallback.responses);
        } finally {
          setIsFetching(false);
        }
      }
    }, 2000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(interval);
    };
  }, [isEligiblePage, language, location.pathname, resetInactivity, showNode, user]);

  const handleNodeClick = () => {
    if (!isExpanded) {
      setIsExpanded(true);
    }
  };

  const handleResponse = async (e, resp) => {
    e.stopPropagation();

    // Animation
    const rect = e.target.getBoundingClientRect();
    spawnParticles(rect.left + rect.width / 2, rect.top + rect.height / 2);

    // API call
    try {
      await api.post('/users/q-connections', { count: 1 });
    } catch (err) {
      console.warn("Failed to update qConnections", err);
    }

    // Hide node
    setShowNode(false);
    setIsExpanded(false);
    lastActivityRef.current = Date.now();
  };

  const spawnParticles = (x, y) => {
    for (let i = 0; i < 20; i++) {
      particlesRef.current.push(new NodeParticle(x, y));
    }
    if (!animationRef.current) {
      animate();
    }
  };

  const animate = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    particlesRef.current = particlesRef.current.filter(p => p.alpha > 0);
    particlesRef.current.forEach(p => {
      p.update();
      p.draw(ctx, '#00ffaa');
    });

    if (particlesRef.current.length > 0) {
      animationRef.current = requestAnimationFrame(animate);
    } else {
      animationRef.current = null;
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (canvasRef.current) {
        canvasRef.current.width = window.innerWidth;
        canvasRef.current.height = window.innerHeight;
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useLayoutEffect(() => {
    if (isExpanded && expandedRef.current) {
      const rect = expandedRef.current.getBoundingClientRect();
      const margin = 20;
      let offX = 0;
      let offY = 0;

      if (rect.right > window.innerWidth - margin) {
        offX = (window.innerWidth - margin) - rect.right;
      }
      if (rect.left < margin) {
        offX = margin - rect.left;
      }
      if (rect.bottom > window.innerHeight - margin) {
        offY = (window.innerHeight - margin) - rect.bottom;
      }
      if (rect.top < margin) {
        offY = margin - rect.top;
      }

      if (offX !== 0 || offY !== 0) {
        setModalOffset({ x: offX, y: offY });
      }
    } else {
      setModalOffset({ x: 0, y: 0 });
    }
  }, [isExpanded]);

  return (
    <>
      <div className="synaptic-nodes-container">
        <AnimatePresence>
          {showNode && (
            <motion.div
              className="synaptic-node"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              style={{ top: `${nodePos.y}%`, left: `${nodePos.x}%` }}
              onClick={handleNodeClick}
              whileHover={{ scale: 1.1 }}
            >
              {!isExpanded ? (
                <motion.div
                  className={`node-core ${isFetching ? 'fetching' : ''}`}
                  animate={isFetching ? {
                    scale: [1, 1.5, 1],
                    boxShadow: [
                      "0 0 20px rgba(0, 255, 170, 0.5)",
                      "0 0 40px rgba(0, 255, 170, 0.8)",
                      "0 0 20px rgba(0, 255, 170, 0.5)"
                    ]
                  } : {
                    scale: [1, 1.2, 1],
                    opacity: [0.7, 1, 0.7]
                  }}
                  transition={{ duration: isFetching ? 0.8 : 2, repeat: Infinity }}
                />
              ) : (
                <motion.div
                  ref={expandedRef}
                  className="node-expanded"
                  initial={{ width: 20, height: 20, borderRadius: "50%" }}
                  animate={{
                    width: "auto",
                    height: "auto",
                    borderRadius: "12px",
                    x: modalOffset.x,
                    y: modalOffset.y
                  }}
                >
                  <p className="node-question">{currentQuestion}</p>
                  <div className="node-responses">
                    {responses.map(resp => (
                      <button
                        key={resp}
                        className="node-response-btn"
                        onClick={(e) => handleResponse(e, resp)}
                      >
                        {resp}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <canvas ref={canvasRef} className="synaptic-particles-canvas" />
    </>
  );
}
